import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Card, CardBody, Col, Row } from 'reactstrap';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { SkeletonTable } from '../../../../../Components/Common/Skeleton';
import OfferTable from '../EvaluationOperational/Components/OfferTable';
import TenderNotClosedAlert from '../EvaluationOperational/Components/TenderNotClosedAlert';
import {
  canAccessEvaluationOperationalData,
  getEvaluationOperationalLockReason,
  isEvaluationSaved,
} from '../../../utils/tenderEvaluationHelpers';
import {
  formatImprovementClosingDate,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../../shared/offer-domain/improvement';
import { DateCountdown } from '../../../../../shared/offer-domain/ui';
import {
  downloadImprovementExcel,
  normalizeImprovementOfferRows,
} from '../../../utils/improvementDocument';

const Improvement = ({
  procurement,
  procId,
  parcels = [],
  isLoadingParcels = false,
  isLoadingProcurement = false,
  closingDate: closingDateProp = null,
  onImprovementClosingDateChange,
}) => {
  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [isImprovementReady, setIsImprovementReady] = useState(false);
  const [isMetaLoading, setIsMetaLoading] = useState(true);

  const handleImprovementMeta = useCallback(
    ({ deliveryDetails: rows, ready = false } = {}) => {
      if (!ready) {
        setIsMetaLoading(true);
        return;
      }
      const nextRows = Array.isArray(rows) ? rows : [];
      setDeliveryDetails(nextRows);
      setIsImprovementReady(true);
      setIsMetaLoading(false);
      const date = resolveImprovementClosingDate(nextRows);
      onImprovementClosingDateChange?.(date);
    },
    [onImprovementClosingDateChange]
  );

  const closingDate = useMemo(
    () => resolveImprovementClosingDate(deliveryDetails) ?? closingDateProp,
    [deliveryDetails, closingDateProp]
  );
  const isImprovementOpen = isImprovementClosingActive(closingDate);
  const showAwaitingGate =
    isImprovementReady && Boolean(closingDate) && isImprovementOpen;
  const improvementRows = useMemo(
    () => normalizeImprovementOfferRows(deliveryDetails),
    [deliveryDetails]
  );
  const isDownloadLoading = isMetaLoading || !isImprovementReady;
  const canDownloadReport =
    isEvaluationSaved(procurement) && improvementRows.length > 0;

  if (isLoadingProcurement && !procurement) {
    return (
      <Card className='border shadow-md mb-4'>
        <CardBody>
          <SkeletonTable rows={5} columns={6} />
        </CardBody>
      </Card>
    );
  }

  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessData = canAccessEvaluationOperationalData(procurement);

  if (!canAccessData) {
    return (
      <TenderNotClosedAlert featureLabel='Improvement' reason={lockReason} />
    );
  }

  const closingCountdown = (
    <DateCountdown
      label='Closing Date Improvement'
      deadline={closingDate}
      openLabel='Improvement closes in'
      closedLabel='Improvement closed'
    />
  );

  if (showAwaitingGate) {
    return (
      <Card className='border shadow-md mb-4'>
        <CardBody>
          <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
            <div>
              <h5 className='fw-bold text-dark mb-1'>Improvement Detail</h5>
              <p className='text-muted small mb-0'>
                List of offers that have been requested for improvement.
              </p>
            </div>
            {closingCountdown}
          </div>
          <div className='mt-3'>
            <TenderNotClosedAlert
              featureLabel='Improvement'
              reason='awaiting'
            />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <div className='mb-4'>
          <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
            <div>
              <h5 className='fw-bold text-dark mb-1'>Improvement Detail</h5>
              <p className='text-muted small mb-0'>
                List of offers that have been requested for improvement.
              </p>
            </div>
            <ActionButton
              variant='secondary'
              icon='ri-download-2-line'
              className='flex-shrink-0'
              loading={isDownloadLoading}
              disabled={!isDownloadLoading && !canDownloadReport}
              onClick={() =>
                downloadImprovementExcel({
                  procurement,
                  rows: deliveryDetails,
                })
              }
            >
              Download
            </ActionButton>
          </div>
          {isImprovementReady && closingDate ? (
            <div className='d-flex flex-nowrap align-items-stretch gap-3 mt-3'>
              <div
                className='flex-grow-1 position-relative'
                style={{ minWidth: 0 }}
              >
                <Alert
                  color='danger'
                  className='position-absolute top-0 bottom-0 start-0 end-0 mb-0 d-flex align-items-center overflow-auto'
                >
                  <div>
                    <h6 className='alert-heading mb-1 fw-semibold'>
                      Improvement has ended
                    </h6>
                    <p className='mb-0 fs-13'>
                      Improvement has ended. It ended on{' '}
                      {formatImprovementClosingDate(closingDate)}. After
                      Lock/Saved Evaluation, select offers in the Negotiation
                      tab.
                    </p>
                  </div>
                </Alert>
              </div>
              <div className='flex-shrink-0'>{closingCountdown}</div>
            </div>
          ) : null}
        </div>
        {!isImprovementReady ? <SkeletonTable rows={5} columns={6} /> : null}
        <Row className={isImprovementReady ? 'mt-3' : 'd-none'}>
          <Col md={12}>
            <OfferTable
              viewMode='improvement'
              procurementId={procurement?.id || procId}
              procurement={procurement}
              parcels={parcels}
              isLoadingParcels={isLoadingParcels}
              onImprovementMeta={handleImprovementMeta}
              isActive
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default Improvement;
